/**
 * Sdílené stavební kameny ploch pod kartou cockpitu („Já za volantem",
 * „Moje pokladna"). Obě plochy mají vypadat jako jedna věc — štítek sekce
 * ve stylu karty, destičky s čísly položené na ploše a řádky seznamu.
 *
 * Materiál destiček je týž jako u dlaždic v hlavičce (`CockpitSlot`): o stupeň
 * světlejší závoj, světlo na horní hraně a stín pod ní.
 */

export function barvyPlochy(isDark) {
    return {
        primarni: isDark ? '#fff' : 'var(--text-primary)',
        tlumena: isDark ? 'rgba(255,255,255,0.58)' : 'var(--text-secondary)',
        slaba: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)',
        deska: isDark
            ? 'linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.045) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.72) 100%)',
        okraj: isDark ? 'rgba(255,255,255,0.09)' : 'var(--border-default)',
        hloubka: isDark
            ? '0 6px 14px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)'
            : '0 6px 14px -8px rgba(30,30,50,0.22), inset 0 1px 0 rgba(255,255,255,0.95)',
    };
}

/** Štítek sekce — verzálky v barvě dlaždice, vpravo doplněk (SPZ, název pokladny). */
export function StitekPlochy({ Ikona, barva, children, vpravo = null, isDark }) {
    const b = barvyPlochy(isDark);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
                flex: 1, minWidth: 0,
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
                textTransform: 'uppercase', color: barva,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
                {Ikona && <Ikona size={13} style={{ flexShrink: 0 }} />}
                {children}
            </span>
            {vpravo && (
                <span style={{
                    flexShrink: 0, fontSize: 12, fontWeight: 600, color: b.tlumena,
                    whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                }}>{vpravo}</span>
            )}
        </div>
    );
}

/** Destička s jedním číslem — velká hodnota, popisek a drobný doplněk. */
export function CisloPlochy({ hodnota, popisek, doplnek = null, barva = null, isDark }) {
    const b = barvyPlochy(isDark);
    return (
        <div style={{
            flex: 1, minWidth: 0,
            display: 'flex', flexDirection: 'column', gap: 1,
            padding: '10px 10px 9px', borderRadius: 11,
            background: b.deska, border: `1px solid ${b.okraj}`, boxShadow: b.hloubka,
        }}>
            <span style={{
                fontSize: 22, fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.1,
                color: barva || b.primarni, fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{hodnota}</span>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: b.tlumena }}>{popisek}</span>
            {doplnek && (
                <span style={{ fontSize: 11, color: b.slaba, fontVariantNumeric: 'tabular-nums' }}>{doplnek}</span>
            )}
        </div>
    );
}

/** Řádek seznamu (jízda, účtenka) — tečka/ikona, hlavní text, pravý doplněk. */
export function RadekPlochy({ znacka, hlavni, vedlejsi = null, vpravo = null, vpravoPod = null, isDark }) {
    const b = barvyPlochy(isDark);
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 11px', borderRadius: 10,
            background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.6)',
            border: `1px solid ${b.okraj}`,
            fontVariantNumeric: 'tabular-nums',
        }}>
            {znacka}
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{
                    fontSize: 13.5, fontWeight: 650, color: b.primarni,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{hlavni}</span>
                {vedlejsi && (
                    <span style={{
                        fontSize: 11.5, color: b.tlumena,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{vedlejsi}</span>
                )}
            </span>
            {(vpravo || vpravoPod) && (
                <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    {vpravo && <span style={{ fontSize: 13, fontWeight: 700, color: b.primarni }}>{vpravo}</span>}
                    {vpravoPod && <span style={{ fontSize: 11, color: b.tlumena }}>{vpravoPod}</span>}
                </span>
            )}
        </div>
    );
}

/** Tečka v barvě stavu (známka jízdy, druh účtenky). */
export function Tecka({ barva }) {
    return <span style={{ width: 9, height: 9, borderRadius: '50%', background: barva, flexShrink: 0 }} />;
}

/**
 * Tlačítko plochy. `hlavni` = plné v barvě (jedna hlavní akce plochy),
 * jinak skleněné. `varovani` = akce, která něco končí (uvolnit auto).
 */
export function TlacitkoPlochy({ children, Ikona, onClick, hlavni = false, barva = '#0CC0DF', varovani = false, disabled = false, isDark }) {
    const b = barvyPlochy(isDark);
    const text = hlavni ? '#062129' : (varovani ? '#F87171' : b.primarni);
    return (
        <button type="button" onClick={onClick} disabled={disabled}
            style={{
                flex: 1, minWidth: 0, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '0 12px', borderRadius: 11,
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                color: disabled ? b.slaba : text,
                background: hlavni && !disabled
                    ? `linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 60%), ${barva}`
                    : b.deska,
                border: hlavni && !disabled ? '1px solid transparent' : `1px solid ${varovani ? 'rgba(248,113,113,0.35)' : b.okraj}`,
                boxShadow: b.hloubka,
                cursor: disabled ? 'not-allowed' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
            {Ikona && <Ikona size={16} style={{ flexShrink: 0 }} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13.5, fontWeight: 700 }}>{children}</span>
        </button>
    );
}

export const fmtKc = (n) => (n == null || isNaN(n) ? '—' : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');
export const fmtKm = (n) => (n == null ? '—' : `${Number(n).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} km`);
export const fmtCas = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
